/*
 *   Copyright (c) 2021 Propally.Inc
 *   All rights reserved.
 */
/**
 * モジュール概要
 * --------------
 * このモジュールは、選択中の物件群に対する月次/年次の収支（バランスシート）と
 * ローン情報を算出し、UI に提供するための Recoil セレクタ群を定義します。
 * 重たい計算は Web Worker 上で実行し、結果は Recoil によりメモ化されます。
 * 直近月の結果とローン情報は、localStorage を用いた軽量なキャッシュにも保存し、
 * 再訪時の読み込みを高速化します。
 *
 * 主要な責務:
 * - 物件ごとの月次バランスシートを Worker 経由で作成（MonthlyBalanceSheets）。
 * - 物件ごとの結果 Map を横断集計し、単一の時系列に縮約（ReducedMonthlyBalanceSheets）。
 * - UI 用にその時系列を抽出/変換（期間抽出、年次合算、当月取得など）。
 * - 「今日のローン情報」を導出し、月次キャッシュとの整合性を維持。
 */
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import _ from "lodash";
import { selector, selectorFamily } from "recoil";

import {
  buildPropertyFingerprintPayload,
  readThisMonthCache,
  writeThisMonthCache,
  readThisMonthLoanInfoCache,
  writeThisMonthLoanInfoCache,
} from "@/utilities/monthCache";

import {
  BalanceSelectedEndingMonth,
  BalanceSelectedStartingMonth,
  ComparisonTargetMonth,
  PropertyHolder,
  PropertyPriceSimulationState,
  PropertySellSimulationState,
  SelectedEndingMonth,
  SelectedPropertyIds,
  SelectedStartingMonth,
  UserState,
} from "./atoms";
import { MonthlyBalanceSheetPrototype } from "./balancesheetPrototype";
import { Properties, SelectedProperties } from "./propertySelectors";
export type { CashflowResultWithLatestResult, MonthlyBalanceSheet } from "./balancesheetTypes";
export { MonthlyBalanceSheetPrototype } from "./balancesheetPrototype";

dayjs.extend(customParseFormat);

const WORKER_TIMEOUT_MS = 30000;

const runWorkerWithTimeout = <T,>(worker: Worker, payload: unknown, timeoutMs = WORKER_TIMEOUT_MS): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      worker.terminate();
      reject(new Error("Worker timeout"));
    }, timeoutMs);

    worker.onmessage = (e) => {
      clearTimeout(timer);
      worker.terminate();
      resolve(e.data as T);
    };
    worker.onerror = (e) => {
      clearTimeout(timer);
      worker.terminate();
      const msg = `Worker error: ${e.message || "unknown"}`;
      reject(new Error(msg));
    };

    worker.postMessage(payload);
  });
};

const parseDateLike = (value?: string | null) => {
  if (!value) return dayjs("");
  const v = String(value).trim();
  const strict = dayjs(
    v,
    ["YYYY/MM/DD", "YYYY/M/D", "YYYY-MM-DD", "YYYY-M-D", "YYYY/MM", "YYYY/M", "YYYY-MM", "YYYY-M"],
    true
  );
  return strict.isValid() ? strict : dayjs(v);
};

/**
 * クライアント側での月次バランスシート計算（WebWorker）を共通化する。
 *
 * 将来、バックエンドが「月次結果/残高推移」を事前計算して返すようになった場合は、
 * この関数の中身を API 取得へ差し替える（あるいはフラグで切替）ことで UI 側の変更を最小化できる。
 */
const computeMonthlyBalanceSheetsOnClient = async (args: {
  user: unknown;
  property: PropertyHolder;
  sell_simulation_state: { enable?: boolean; sell_at?: string; price?: number };
  price_simulation_state: {
    enablePriceSimulationFromProposal?: boolean;
    enableBalanceSimulationFromProposal?: boolean;
  };
  start_month: string;
  end_month: string;
}) => {
  if (window.Worker === undefined) throw new Error("このブラウザはWebWorkerをサポートしていません。");
  return runWorkerWithTimeout<Map<string, MonthlyBalanceSheet>>(
    new Worker(new URL("../logics/calculateCumulativeBalanceSheet.ts", import.meta.url), { type: "module" }),
    {
      user: args.user,
      property: args.property,
      property_sell_simulation_state: args.sell_simulation_state,
      property_price_simulation_state: args.price_simulation_state,
      start_month: args.start_month,
      end_month: args.end_month,
    }
  );
};

// 物件単位の再利用（クライアント内キャッシュ）
// - Recoil selector の再評価が走っても、同一条件なら Worker 計算を Promise 単位で共有して再計算を避ける。
// - メモリ上限/破棄戦略は現状なし（将来必要なら LRU 化する）。
const monthlyBalanceSheetsPromiseCache = new Map<string, Promise<Map<string, MonthlyBalanceSheet>>>();

// user_input_monthly_cashflow_results の内容が変わってもキャッシュが当たり続けないように、簡易フィンガープリントをキーに含める。
// - 配列 identity が同じ間は WeakMap で再利用し、selector 再評価の都度ハッシュ計算を繰り返さない。
// - 将来サーバ側で月次結果を返すようになれば、そもそもこのフィンガープリント自体が不要になる想定。
const monthlyCashflowResultsFingerprintCache = new WeakMap<Array<MonthlyCashflowResult>, string>();

const FNV1A_32_OFFSET_BASIS = 2166136261;

const fnv1a32 = (hash: number, text: string) => {
  let h = hash >>> 0;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const getMonthlyCashflowResultsFingerprint = (results: Array<MonthlyCashflowResult>) => {
  const cached = monthlyCashflowResultsFingerprintCache.get(results);
  if (cached) return cached;

  // month が未設定の要素が混在しても安定するように sort してからハッシュ化する
  const sorted = [...results].sort((a, b) => String(a.month ?? "").localeCompare(String(b.month ?? "")));
  let hash = FNV1A_32_OFFSET_BASIS;

  for (const r of sorted) {
    const keys = Object.keys(r as any).sort();
    for (const k of keys) {
      const v = (r as any)[k];
      hash = fnv1a32(hash, k);
      hash = fnv1a32(hash, "=");
      hash = fnv1a32(hash, v === undefined ? "u" : v === null ? "n" : String(v));
      hash = fnv1a32(hash, ";");
    }
    hash = fnv1a32(hash, "|");
  }

  const fingerprint = hash.toString(16);
  monthlyCashflowResultsFingerprintCache.set(results, fingerprint);
  return fingerprint;
};

const getSimulationConfigsFingerprint = (configs: PropertyHolder["simulation_configs"]) => {
  if (!configs || configs.length === 0) return "none";
  const sorted = [...configs].sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0));
  let hash = FNV1A_32_OFFSET_BASIS;
  for (const conf of sorted) {
    const keys = Object.keys(conf as any).sort();
    for (const k of keys) {
      const v = (conf as any)[k];
      hash = fnv1a32(hash, k);
      hash = fnv1a32(hash, "=");
      hash = fnv1a32(hash, v === undefined ? "u" : v === null ? "n" : String(v));
      hash = fnv1a32(hash, ";");
    }
    hash = fnv1a32(hash, "|");
  }
  return hash.toString(16);
};

const getPropertyHolderFingerprint = (propertyHolder: PropertyHolder) => {
  const payload = buildPropertyFingerprintPayload([propertyHolder]);
  return fnv1a32(FNV1A_32_OFFSET_BASIS, JSON.stringify(payload)).toString(16);
};

const getUserCalculationFingerprint = (user: unknown) => {
  const payload = {
    income_tax_rate: (user as { income_tax_rate?: number | null } | null)?.income_tax_rate,
  };
  return fnv1a32(FNV1A_32_OFFSET_BASIS, JSON.stringify(payload)).toString(16);
};

const buildMonthlyBalanceSheetsCacheKey = (args: {
  purpose: "default" | "balance";
  user: unknown;
  property: PropertyHolder;
  start: Dayjs;
  end: Dayjs;
  sell_simulation_state: { enable?: boolean; sell_at?: string; price?: number };
  price_simulation_state: {
    enablePriceSimulationFromProposal?: boolean;
    enableBalanceSimulationFromProposal?: boolean;
  };
}) => {
  const propertyFingerprint = getPropertyHolderFingerprint(args.property);
  const userFingerprint = getUserCalculationFingerprint(args.user);
  const userInputs = args.property.user_input_monthly_cashflow_results ?? [];
  const userInputFingerprint = getMonthlyCashflowResultsFingerprint(userInputs);
  const simulationConfigsFingerprint = getSimulationConfigsFingerprint(args.property.simulation_configs);
  const sellKey = `${args.sell_simulation_state?.enable ? 1 : 0}:${args.sell_simulation_state?.sell_at ?? ""}:${
    args.sell_simulation_state?.price ?? ""
  }`;
  const priceKey = `${args.price_simulation_state?.enablePriceSimulationFromProposal ? 1 : 0}:${
    args.price_simulation_state?.enableBalanceSimulationFromProposal ? 1 : 0
  }`;
  return [
    args.purpose,
    args.property.id,
    args.start.format("YYYY/MM"),
    args.end.format("YYYY/MM"),
    sellKey,
    priceKey,
    propertyFingerprint,
    userFingerprint,
    userInputFingerprint,
    simulationConfigsFingerprint,
  ].join("|");
};

const getMonthlyBalanceSheetsCached = async (args: {
  purpose: "default" | "balance";
  user: unknown;
  property: PropertyHolder;
  sell_simulation_state: { enable?: boolean; sell_at?: string; price?: number };
  price_simulation_state: {
    enablePriceSimulationFromProposal?: boolean;
    enableBalanceSimulationFromProposal?: boolean;
  };
  start: Dayjs;
  end: Dayjs;
}) => {
  const key = buildMonthlyBalanceSheetsCacheKey({
    purpose: args.purpose,
    user: args.user,
    property: args.property,
    start: args.start,
    end: args.end,
    sell_simulation_state: args.sell_simulation_state,
    price_simulation_state: args.price_simulation_state,
  });

  const cached = monthlyBalanceSheetsPromiseCache.get(key);
  if (cached) return cached;

  const promise = computeMonthlyBalanceSheetsOnClient({
    user: args.user,
    property: args.property,
    sell_simulation_state: args.sell_simulation_state,
    price_simulation_state: args.price_simulation_state,
    start_month: args.start.format("YYYY/MM"),
    end_month: args.end.format("YYYY/MM"),
  });
  monthlyBalanceSheetsPromiseCache.set(key, promise);
  return promise;
};

/**
 * Properties と SelectedProperties の関係（簡潔版）
 * - Properties: 全物件集合（選択状態とは独立）
 * - SelectedProperties: UI で選択中の物件集合（SelectedPropertyIds と一致）
 * - キャッシュ/集計は基本 SelectedProperties に合わせる。
 *   Properties をキーにすると、選択変更後も「以前の選択で計算された値」を再利用し、
 *   一部物件だけ合算される等の不整合が起きうる。
 */

/**
 * 各物件の「今月のバランスシート」をMapで返すセレクタ。
 * key: 物件ID, value: 今月分MonthlyBalanceSheet
 */
export const PropertyIdToThisMonthlyBalanceSheet = selectorFamily<Map<number, MonthlyBalanceSheet>, null>({
  key: "property_id_to_this_monthly_balance_sheet",
  get:
    () =>
    async ({ get }) => {
      const this_month = dayjs().format("YYYY/MM");
      return get(Properties).reduce((map, property) => {
        const monthly_balance_sheets = get(MonthlyBalanceSheets(property.id));
        const this_monthly_balance_sheet = monthly_balance_sheets.get(this_month);
        // {key: 物件ID, value: 今月分balance_sheet} のMapで返却
        map.set(property.id, this_monthly_balance_sheet);
        return map;
      }, new Map<number, MonthlyBalanceSheet>());
    },
});

/**
 * 指定した property_id の「全期間の月次バランスシート(Map<YYYY/MM, MonthlyBalanceSheet>)」を計算して返す。
 * 計算は WebWorker(../logics/calculateCumulativeBalanceSheet.ts) で非同期実行し、ポーリングで完了を待つ。
 * 注意: 返り値は promise（worker完了後にresolveした値）そのものを返す設計。
 */
export const MonthlyBalanceSheets = selectorFamily<Map<string, MonthlyBalanceSheet>, number>({
  key: "monthly_balance_sheets",
  get:
    (property_id) =>
    async ({ get }) => {
      const user = get(UserState);
      const target_properties = get(Properties);
      const selectedStartAt = get(SelectedStartingMonth).startOf("month");
      const selectedEndAt = get(SelectedEndingMonth).startOf("month");
      const thisMonth = dayjs().startOf("month");

      // 対象物件の取得と存在チェック
      const property = target_properties.find((property) => {
        return property.id === property_id;
      });
      if (property === undefined) {
        // 指定IDの物件データが存在しない
        throw new Error(`指定した物件IDのデータが見つかりません: ${property_id}`);
      }

      // 売却・価格シミュレーションの状態を取得
      const sell_simulation_state = get(PropertySellSimulationState(property.id));
      const price_simulation_state = get(PropertyPriceSimulationState(property.id));

      const boughtAt = property.property.bought_at ? parseDateLike(property.property.bought_at).startOf("month") : null;
      const paymentStartsAt = property.property.payment_starts_at
        ? parseDateLike(property.property.payment_starts_at).startOf("month")
        : null;
      const sellAt = sell_simulation_state.enable
        ? parseDateLike(sell_simulation_state.sell_at).startOf("month")
        : null;

      const minMonth = (a: Dayjs, b: Dayjs) => (a.isBefore(b) ? a : b);
      const maxMonth = (a: Dayjs, b: Dayjs) => (a.isAfter(b) ? a : b);

      // ReducedMonthlyBalanceSheets / MonthlyCashflowResultsOnToday でも「今月」を必要とするため、選択期間外でも今月は含める
      // NOTE: worker は Mapキー=YYYY/MM（その月）を返す。購入月/返済開始月が計算範囲に入るよう候補に含める。
      const startCandidates: Dayjs[] = [selectedStartAt, thisMonth];
      if (boughtAt && boughtAt.isValid()) startCandidates.push(boughtAt);
      if (paymentStartsAt && paymentStartsAt.isValid()) startCandidates.push(paymentStartsAt);
      const endCandidates: Dayjs[] = [selectedEndAt, thisMonth];
      if (sellAt && sellAt.isValid()) endCandidates.push(sellAt);

      const keyStartMonth = startCandidates.reduce((acc, v) => minMonth(acc, v), selectedStartAt);
      const keyEndMonth = endCandidates.reduce((acc, v) => maxMonth(acc, v), selectedEndAt);

      const promise = getMonthlyBalanceSheetsCached({
        purpose: "default",
        user: user,
        property: property,
        sell_simulation_state: sell_simulation_state,
        price_simulation_state: price_simulation_state,
        start: keyStartMonth,
        end: keyEndMonth,
      });
      await promise;
      return promise;
    },
});

/**
 * /balance 画面専用: 指定した property_id の「全期間の月次バランスシート」を計算して返す。
 * SelectedStarting/EndingMonth とは別の BalanceSelectedStarting/EndingMonth を参照する。
 */
export const MonthlyBalanceSheetsForBalance = selectorFamily<Map<string, MonthlyBalanceSheet>, number>({
  key: "monthly_balance_sheets_for_balance",
  get:
    (property_id) =>
    async ({ get }) => {
      const user = get(UserState);
      const target_properties = get(Properties);
      const selectedStartAt = get(BalanceSelectedStartingMonth).startOf("month");
      const selectedEndAt = get(BalanceSelectedEndingMonth).startOf("month");
      const thisMonth = dayjs().startOf("month");

      const property = target_properties.find((property) => {
        return property.id === property_id;
      });
      if (property === undefined) {
        throw new Error(`指定した物件IDのデータが見つかりません: ${property_id}`);
      }

      const sell_simulation_state = get(PropertySellSimulationState(property.id));
      const price_simulation_state = get(PropertyPriceSimulationState(property.id));

      const boughtAt = property.property.bought_at ? parseDateLike(property.property.bought_at).startOf("month") : null;
      const paymentStartsAt = property.property.payment_starts_at
        ? parseDateLike(property.property.payment_starts_at).startOf("month")
        : null;
      const sellAt = sell_simulation_state.enable
        ? parseDateLike(sell_simulation_state.sell_at).startOf("month")
        : null;

      const minMonth = (a: Dayjs, b: Dayjs) => (a.isBefore(b) ? a : b);
      const maxMonth = (a: Dayjs, b: Dayjs) => (a.isAfter(b) ? a : b);

      // ReducedMonthlyBalanceSheetsForBalance / 表示要件のため、選択期間外でも今月は含める
      // NOTE: worker は Mapキー=YYYY/MM（その月）を返す。購入月/返済開始月が計算範囲に入るよう候補に含める。
      const startCandidates: Dayjs[] = [selectedStartAt, thisMonth];
      if (boughtAt && boughtAt.isValid()) startCandidates.push(boughtAt);
      if (paymentStartsAt && paymentStartsAt.isValid()) startCandidates.push(paymentStartsAt);
      const endCandidates: Dayjs[] = [selectedEndAt, thisMonth];
      if (sellAt && sellAt.isValid()) endCandidates.push(sellAt);

      const keyStartMonth = startCandidates.reduce((acc, v) => minMonth(acc, v), selectedStartAt);
      const keyEndMonth = endCandidates.reduce((acc, v) => maxMonth(acc, v), selectedEndAt);

      const promise = getMonthlyBalanceSheetsCached({
        purpose: "balance",
        user: user,
        property: property,
        sell_simulation_state: sell_simulation_state,
        price_simulation_state: price_simulation_state,
        start: keyStartMonth,
        end: keyEndMonth,
      });
      await promise;
      return promise;
    },
});

/**
 * 複数物件の「全期間・全月の結果」を合算し、月キー(YYYY/MM)ごとの結果Mapに還元したものを返す。
 * 合算は WebWorker(../logics/calculateReducedMonthlyCashflowResult.ts) を用いる。
 * 計算後、当月分をキャッシュに保存する。
 */
export const ReducedMonthlyBalanceSheets = selectorFamily<Map<string, MonthlyBalanceSheet>, null>({
  key: "reduced_monthly_results",
  get:
    () =>
    async ({ get }) => {
      // 集計スコープは UI の選択集合。
      // ここで得た SelectedProperties を、この後の当月キャッシュ（writeThisMonthCache）のキーにも使う。
      const user = get(UserState);
      const selectedProperties = get(SelectedProperties);
      const property_ids = get(SelectedPropertyIds);
      const selectedStartAt = get(SelectedStartingMonth).startOf("month");
      const selectedEndAt = get(SelectedEndingMonth).startOf("month");
      const thisMonth = dayjs().startOf("month");

      const minMonth = (a: Dayjs, b: Dayjs) => (a.isBefore(b) ? a : b);
      const maxMonth = (a: Dayjs, b: Dayjs) => (a.isAfter(b) ? a : b);

      // 期間抽出/今日の表示/シミュレーションなどに必要な範囲のみを合算する（SelectableMin/Maxの全期間は重すぎる）
      const keyStartMonth = [selectedStartAt, thisMonth].reduce((acc, v) => minMonth(acc, v), selectedStartAt);
      const keyEndMonth = [selectedEndAt, thisMonth].reduce((acc, v) => maxMonth(acc, v), selectedEndAt);

      // 物件ごとの「全月Map」を取得し配列化
      const property_results = property_ids.map((property_id) => {
        return get(MonthlyBalanceSheets(property_id));
      });

      if (window.Worker === undefined) throw new Error("このブラウザはWebWorkerをサポートしていません。");
      const result = await runWorkerWithTimeout<Map<string, MonthlyBalanceSheet>>(
        new Worker(new URL("../logics/calculateReducedMonthlyCashflowResult.ts", import.meta.url), { type: "module" }),
        {
          property_results: property_results,
          start_month: keyStartMonth.format("YYYY/MM"),
          end_month: keyEndMonth.format("YYYY/MM"),
        }
      );

      // 当月の収支をキャッシュへ書き込み（失敗は黙殺）
      try {
        const thisMonthKey = dayjs().startOf("month").format("YYYY/MM");
        const thisMonth = result.get(thisMonthKey);
        if (thisMonth) writeThisMonthCache(selectedProperties, thisMonth, user);
      } catch (_) {
        // 特に何もしない
      }

      return result;
    },
});

/**
 * /balance 画面専用: 複数物件の「全期間・全月の結果」を合算し、月キー(YYYY/MM)ごとの結果Mapに還元。
 * BalanceSelectedStarting/EndingMonth を用いて合算スコープを短期化する。
 */
export const ReducedMonthlyBalanceSheetsForBalance = selectorFamily<Map<string, MonthlyBalanceSheet>, null>({
  key: "reduced_monthly_results_for_balance",
  get:
    () =>
    async ({ get }) => {
      const user = get(UserState);
      const selectedProperties = get(SelectedProperties);
      const property_ids = get(SelectedPropertyIds);
      const selectedStartAt = get(BalanceSelectedStartingMonth).startOf("month");
      const selectedEndAt = get(BalanceSelectedEndingMonth).startOf("month");
      const thisMonth = dayjs().startOf("month");

      const minMonth = (a: Dayjs, b: Dayjs) => (a.isBefore(b) ? a : b);
      const maxMonth = (a: Dayjs, b: Dayjs) => (a.isAfter(b) ? a : b);

      const keyStartMonth = [selectedStartAt, thisMonth].reduce((acc, v) => minMonth(acc, v), selectedStartAt);
      const keyEndMonth = [selectedEndAt, thisMonth].reduce((acc, v) => maxMonth(acc, v), selectedEndAt);

      const property_results = property_ids.map((property_id) => {
        return get(MonthlyBalanceSheetsForBalance(property_id));
      });

      if (window.Worker === undefined) throw new Error("このブラウザはWebWorkerをサポートしていません。");
      const result = await runWorkerWithTimeout<Map<string, MonthlyBalanceSheet>>(
        new Worker(new URL("../logics/calculateReducedMonthlyCashflowResult.ts", import.meta.url), { type: "module" }),
        {
          property_results: property_results,
          start_month: keyStartMonth.format("YYYY/MM"),
          end_month: keyEndMonth.format("YYYY/MM"),
        }
      );

      try {
        const thisMonthKey = dayjs().startOf("month").format("YYYY/MM");
        const thisMonth = result.get(thisMonthKey);
        if (thisMonth) writeThisMonthCache(selectedProperties, thisMonth, user);
      } catch (_) {
        // noop
      }

      return result;
    },
});

/**
 * 選択期間(開始～終了)に該当する月の配列を返す。
 * ReducedMonthlyBalanceSheets からフィルタして抽出。
 */
export const MonthlyCashflowResultsOnSelectedPeriod = selector({
  key: "monthly_results_on_selected_period",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    const start_at = get(SelectedStartingMonth);
    const end_at = get(SelectedEndingMonth);

    // 全体取得
    const whole_results = get(ReducedMonthlyBalanceSheets(null));
    const results: MonthlyBalanceSheet[] = [];

    // 期間内の月のみ抽出
    whole_results.forEach((r, month) => {
      const is_in_the_period =
        ((start_at.isBefore(dayjs(month)) || start_at.isSame(dayjs(month))) && end_at.isAfter(dayjs(month))) ||
        end_at.isSame(dayjs(month));

      if (is_in_the_period) results.push(r);
    });
    return results;
  },
});

export const MonthlyCashflowResultsOnBalanceSelectedPeriod = selector({
  key: "monthly_results_on_balance_selected_period",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    const start_at = get(BalanceSelectedStartingMonth);
    const end_at = get(BalanceSelectedEndingMonth);

    const whole_results = get(ReducedMonthlyBalanceSheetsForBalance(null));
    const results: MonthlyBalanceSheet[] = [];

    whole_results.forEach((r, month) => {
      const is_in_the_period =
        ((start_at.isBefore(dayjs(month)) || start_at.isSame(dayjs(month))) && end_at.isAfter(dayjs(month))) ||
        end_at.isSame(dayjs(month));
      if (is_in_the_period) results.push(r);
    });
    return results;
  },
});

/**
 * 選択期間の「月次配列 + 年合計(最後尾に1件)」を返す。
 * 年合計は単純加算。特定キー(excludeKeys)は年合計対象から除外する。
 */
export const MonthlyCashflowResultsOnSelectedPeriodWithYearlySum = selector({
  key: "monthly_results_on_selected_period_with_yearly_sim",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    // 1月~12月分配列取得
    const monthly = get(MonthlyCashflowResultsOnSelectedPeriod);
    const results = _.cloneDeep(monthly);
    if (results.length === 0) return [];

    // 年合計格納用変数（プロトタイプをコピー）
    const yearly = _.cloneDeep(MonthlyBalanceSheetPrototype);

    // 年合計に含めないキー（累計や売却関連、期中状態など）
    const excludeKeys = [
      "cumulative",
      "remaining_loan_amount",
      "simulated",
      "selling_agent_fee",
      "stamp_tax",
      "misc_selling_expense",
    ];

    // 最後尾に年合計を追加（配列内の各月を集計）
    monthly.forEach((r) => {
      // JSの負インデックスは未定義なので、この条件は常にfalse。挙動変更せずそのまま。
      if (r === monthly[-1]) return;

      Object.keys(yearly).forEach((key) => {
        if (excludeKeys.some((k) => key.includes(k))) return;

        if (key === "result") {
          Object.keys(yearly.result).forEach((k) => {
            if (!yearly.result[k]) yearly.result[k] = 0;
            if (k !== "month") yearly.result[k] += r.result[k];
          });
        } else if (key !== "month") {
          yearly[key] += r[key];
        }
      });
    });
    results.push(yearly);

    return results;
  },
});

export const MonthlyCashflowResultsOnBalanceSelectedPeriodWithYearlySum = selector({
  key: "monthly_results_on_balance_selected_period_with_yearly_sum",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    const monthly = get(MonthlyCashflowResultsOnBalanceSelectedPeriod);
    const results = _.cloneDeep(monthly);
    if (results.length === 0) return [];

    const yearly = _.cloneDeep(MonthlyBalanceSheetPrototype);
    const excludeKeys = [
      "cumulative",
      "remaining_loan_amount",
      "simulated",
      "selling_agent_fee",
      "stamp_tax",
      "misc_selling_expense",
    ];

    monthly.forEach((r) => {
      // JSの負インデックスは未定義なので、この条件は常にfalse。挙動変更せずそのまま。
      if (r === monthly[-1]) return;

      Object.keys(yearly).forEach((key) => {
        if (excludeKeys.some((k) => key.includes(k))) return;

        if (key === "result") {
          Object.keys(yearly.result).forEach((k) => {
            if (!yearly.result[k]) yearly.result[k] = 0;
            if (k !== "month") yearly.result[k] += r.result[k];
          });
        } else if (key !== "month") {
          yearly[key] += r[key];
        }
      });
    });
    results.push(yearly);

    return results;
  },
});

/**
 * 選択期間の年単位サマリを返す。
 * 各年の1月(月index=0)に達したら新しい年のレコードをpushし、それ以降は同年分を加算していく。
 */
export const YearlyCashflowResultsOnSelectedPeriod = selector({
  key: "yearly_results_on_selected_period",
  get: ({ get }): Array<MonthlyBalanceSheet> => {
    const start_at = get(SelectedStartingMonth).startOf("year");
    const end_at = get(SelectedEndingMonth).endOf("year");

    const whole_results = get(ReducedMonthlyBalanceSheets(null));
    const results: MonthlyBalanceSheet[] = [];

    whole_results.forEach((r, month) => {
      const dmonth = dayjs(month);
      const is_in_the_period =
        ((start_at.isBefore(dmonth) || start_at.isSame(dmonth)) && end_at.isAfter(dmonth)) || end_at.isSame(dmonth);
      const is_the_beginning_of_this_year = dmonth.month() === 0;

      if (is_in_the_period) {
        if (is_the_beginning_of_this_year) {
          // 年の先頭月なら新規レコードとしてpush
          const result = _.cloneDeep(r);
          result.month = dmonth;
          results.push(result);
        } else {
          // 同年内は直近の年レコードに加算
          const saved = results[results.length - 1];
          for (const key in saved) {
            if (key === "result") {
              for (const k in saved.result) {
                if (k !== "month") saved.result[k] += r.result[k];
              }
            } else if (key !== "month") {
              saved[key] += r[key];
            }
          }
        }
      }
    });
    return results;
  },
});

/**
 * 今日(当月)の月次結果を返す。ReducedMonthlyBalanceSheets から当月キーで取得。
 */
export const MonthlyCashflowResultsOnToday = selector({
  key: "monthly_results_on_today",
  get: ({ get }): MonthlyBalanceSheet => {
    const this_month = dayjs().startOf("month").format("YYYY/MM");
    const property_ids = get(SelectedPropertyIds);

    const reduced = _.cloneDeep(MonthlyBalanceSheetPrototype);
    reduced.month = dayjs(this_month);

    property_ids.forEach((property_id) => {
      const sheet = get(MonthlyBalanceSheets(property_id)).get(this_month);
      if (!sheet) return;

      for (const key in sheet) {
        if (key === "result") {
          for (const k in sheet.result) {
            if (k !== "month") {
              if (reduced.result[k] === undefined) reduced.result[k] = 0;
              if (!Number.isNaN(sheet.result[k]) && sheet.result[k] !== undefined) reduced.result[k] += sheet.result[k];
            }
          }
        } else if (key !== "month") {
          if (reduced[key] === undefined) reduced[key] = 0;
          if (!Number.isNaN(sheet[key]) && sheet[key] !== undefined) reduced[key] += sheet[key];
        }
      }
    });

    return reduced;
  },
});

/**
 * 今日時点のローン情報を返す。
 * - まずキャッシュを優先して参照（整合性チェックの上、必要なら補正して再キャッシュ）
 * - キャッシュが無い/不足している場合は各物件の今月結果から集計
 * - 集計後はキャッシュに保存
 */
export const LoanInformationOnToday = selector({
  key: "loan_information_on_today",
  get: ({ get }) => {
    const this_month = dayjs().startOf("month").format("YYYY/MM");
    const user = get(UserState);
    const property_ids = get(SelectedPropertyIds);
    // 重要: キャッシュのスコープは UI の選択集合と一致させる
    // → キャッシュキー/整合性チェックには全物件(Properties)ではなく SelectedProperties を使う
    const target_properties = get(SelectedProperties);
    const selectedPropertySet = new Set(property_ids);
    const selectedProperties = target_properties.filter((p): p is PropertyHolder => !!p && selectedPropertySet.has(p.id));
    const loanCandidateCount = selectedProperties.filter((p) => (p.property?.loan_amount ?? 0) > 0).length;

    // キャッシュデータがあれば、キャッシュを読み込む
    const cached = readThisMonthLoanInfoCache<any>(target_properties);
    if (cached) {
      try {
        // 月次キャッシュとローンキャッシュの整合性チェック
        // 同じ選択スコープで月次キャッシュを読み、残債値の整合性を確認
        const monthly = readThisMonthCache<MonthlyBalanceSheet>(target_properties, user);
        const monthlyRemaining = monthly?.remaining_loan_amount;
        const needsFix =
          typeof monthlyRemaining === "number" &&
          monthlyRemaining > 0 &&
          cached.total_loan_remaining !== monthlyRemaining;

        // total_loan_remaining の整合性だけ補正して返す（他は既存値を尊重）
        if (needsFix) {
          const fixed = {
            ...cached,
            total_loan_remaining: monthlyRemaining,
            number_of_loan_contracts:
              cached.number_of_loan_contracts && cached.number_of_loan_contracts > 0
                ? cached.number_of_loan_contracts
                : Math.max(cached.properties?.length ?? 0, 0),
          };
          writeThisMonthLoanInfoCache(target_properties, fixed);
          return fixed;
        }
      } catch (_) {
        // 取得や整合性チェックに失敗した場合はキャッシュそのまま返す
      }
      // 候補としてローンがありそうなのに total_loan_remaining が0のまま、または契約数/内訳が不足している場合は再計算して更新
      try {
        const cachedContracts = cached.number_of_loan_contracts ?? cached.properties?.length ?? 0;
        const cachedPropertyIds = new Set<number>((cached.properties ?? []).map((p) => p.id).filter((id) => typeof id === "number"));
        const missingAny = selectedProperties.some((p) => (p.property?.loan_amount ?? 0) > 0 && !cachedPropertyIds.has(p.id));
        const needsRecalc =
          loanCandidateCount > 0 &&
          (((cached.total_loan_remaining ?? 0) === 0) || cachedContracts !== loanCandidateCount || missingAny);

        if (needsRecalc) {
          const response = {
            number_of_loan_contracts: 0,
            total_amount: 0,
            monthly_payment_amount: 0,
            total_loan_remaining: 0,
            properties: [],
          } as any;

          selectedProperties.forEach((property) => {
            if ((property.property?.loan_amount ?? 0) <= 0) return;

            const results = get(MonthlyBalanceSheets(property.id));
            const this_month_result = results.get(this_month);
            const remainingRaw = this_month_result?.remaining_loan_amount;
            const remaining =
              typeof remainingRaw === "number" && !Number.isNaN(remainingRaw) ? Math.max(remainingRaw, 0) : 0;

            response.number_of_loan_contracts++;
            response.total_amount += property.property.loan_amount;
            response.monthly_payment_amount +=
              (this_month_result?.result.loan_interest ?? 0) +
              (this_month_result?.result.loan_principal ?? 0) +
              (this_month_result?.result.loan_prepayment ?? 0);
            response.total_loan_remaining += remaining;
            response.properties.push({
              id: property.id,
              name: property.property.name,
              remaining_loan_amount: remaining,
            });
          });
          writeThisMonthLoanInfoCache(target_properties, response);
          return response;
        }
      } catch (_) {
        // ignore and return cached
      }
      return cached;
    }

    // キャッシュが無い場合はゼロ初期化して集計開始
    const response = {
      number_of_loan_contracts: 0,
      total_amount: 0,
      monthly_payment_amount: 0,
      total_loan_remaining: 0,
      properties: [],
    };

    selectedProperties.forEach((property) => {
      if ((property.property?.loan_amount ?? 0) <= 0) return;

      const results = get(MonthlyBalanceSheets(property.id));
      const this_month_result = results.get(this_month);
      const remainingRaw = this_month_result?.remaining_loan_amount;
      const remaining = typeof remainingRaw === "number" && !Number.isNaN(remainingRaw) ? Math.max(remainingRaw, 0) : 0;

      response.number_of_loan_contracts++;
      response.total_amount += property.property.loan_amount;
      response.monthly_payment_amount +=
        (this_month_result?.result.loan_interest ?? 0) +
        (this_month_result?.result.loan_principal ?? 0) +
        (this_month_result?.result.loan_prepayment ?? 0);
      response.total_loan_remaining += remaining;

      response.properties.push({
        id: property.id,
        name: property.property.name,
        remaining_loan_amount: remaining,
      });
    });

    // ローン情報について、キャッシュに保存する
    writeThisMonthLoanInfoCache(target_properties, response);
    return response;
  },
});

/**
 * 比較対象月(ComparisonTargetMonth)の各物件のMonthlyBalanceSheetを取得して配列で返す。
 * UIでの横比較に利用。
 */
export const MonthlyBalanceSheetsOnComparisonTargetMonth = selector({
  key: "MonthlyBalanceSheetsOnComparisonTargetMonth",
  get: ({ get }) => {
    const comparisonTargetMonth = get(ComparisonTargetMonth);
    const properties = get(SelectedProperties);
    return properties.map((property) => {
      return {
        property: property,
        result: get(MonthlyBalanceSheets(property.id)).get(comparisonTargetMonth.format("YYYY/MM")),
      };
    });
  },
});

/**
 * キャッシュから取得した今月の収支結果を返す。
 * キャッシュが無い場合は ReducedMonthlyBalanceSheets から当月を取得して返す。
 */
export const CachedMonthlyBalanceSheetOnToday = selector<MonthlyBalanceSheet>({
  key: "cached_monthly_results_on_today",
  get: ({ get }) => {
    const user = get(UserState);
    const properties = get(SelectedProperties);
    const cached = readThisMonthCache<MonthlyBalanceSheet>(properties, user);
    if (cached) return cached;
    // キャッシュがなかった場合は、ここで値を計算して返す
    return get(MonthlyCashflowResultsOnToday);
  },
});
